import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Fab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentsIcon from '@mui/icons-material/Payments';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DataTable, type DataTableColumn, type QuickFilter } from '@/components/common';
import { WarrantyClaimDialog } from '@/components/inventory';
import {
  useWarrantyClaims,
  useUpdateWarrantyClaimStatus,
  type WarrantyClaim,
  type WarrantyClaimStatus,
} from '@/hooks';
import { useNotify } from '@/stores/notificationStore';
import { dateFnsLocale } from '@/utils/locale';

type TabValue = 'all' | WarrantyClaimStatus;

// Status → MUI chip colour. No user-visible strings, so module scope is fine.
const statusColors: Record<WarrantyClaimStatus, 'default' | 'info' | 'success' | 'error'> = {
  open: 'default',
  submitted: 'info',
  approved: 'success',
  rejected: 'error',
  refunded: 'success',
};

const STATUS_ORDER: WarrantyClaimStatus[] = ['open', 'submitted', 'approved', 'rejected', 'refunded'];

const CLAIM_TYPE_LABEL_KEY: Record<string, string> = {
  defective: 'warranty.typeDefective',
  premature_failure: 'warranty.typePrematureFailure',
  wrong_part: 'warranty.typeWrongPart',
};

export default function WarrantyClaimsPage() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isMobile = useIsMobile();

  const claimsQuery = useWarrantyClaims();
  const updateStatus = useUpdateWarrantyClaimStatus();

  const claims = useMemo(() => claimsQuery.data ?? [], [claimsQuery.data]);

  const [tab, setTab] = useState<TabValue>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuClaim, setMenuClaim] = useState<WarrantyClaim | null>(null);

  const statusLabels: Record<WarrantyClaimStatus, string> = {
    open: t('warranty.status.open'),
    submitted: t('warranty.status.submitted'),
    approved: t('warranty.status.approved'),
    rejected: t('warranty.status.rejected'),
    refunded: t('warranty.status.refunded'),
  };

  const filtered = useMemo(
    () => (tab === 'all' ? claims : claims.filter((c) => c.status === tab)),
    [claims, tab],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<TabValue, number> = {
      all: claims.length,
      open: 0,
      submitted: 0,
      approved: 0,
      rejected: 0,
      refunded: 0,
    };
    for (const c of claims) counts[c.status]++;
    return counts;
  }, [claims]);

  const quickFilters: QuickFilter[] = [
    { label: `${tc('all')} (${String(statusCounts.all)})`, value: 'all' },
    ...STATUS_ORDER.map((s) => ({
      label: `${statusLabels[s]} (${String(statusCounts[s])})`,
      value: s,
    })),
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, c: WarrantyClaim) => {
    setAnchorEl(event.currentTarget);
    setMenuClaim(c);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuClaim(null);
  };

  const handleTransition = (c: WarrantyClaim, status: WarrantyClaimStatus, refundAmountCents?: number) => {
    updateStatus.mutate(
      { id: c.id, status, refundAmountCents },
      {
        onSuccess: () => notify.success(t('warranty.statusUpdated'), statusLabels[status]),
        onError: () => notify.error(tc('error'), t('warranty.statusUpdateFailed')),
      },
    );
    handleMenuClose();
  };

  const formatPrice = (cents: number | null) => (cents === null ? '—' : `€${(cents / 100).toFixed(2)}`);

  const columns: DataTableColumn<WarrantyClaim>[] = [
    {
      header: t('warranty.colPart'),
      renderCell: (c) => (
        <Typography variant="body2" fontWeight={600}>
          {c.partName}
        </Typography>
      ),
    },
    {
      header: t('warranty.colSupplier'),
      renderCell: (c) => <Typography variant="body2">{c.supplierName}</Typography>,
    },
    {
      header: t('warranty.colType'),
      renderCell: (c) => (
        <Typography variant="body2">{t(CLAIM_TYPE_LABEL_KEY[c.claimType] ?? c.claimType)}</Typography>
      ),
    },
    {
      header: t('warranty.colStatus'),
      renderCell: (c) => <Chip label={statusLabels[c.status]} size="small" color={statusColors[c.status]} />,
    },
    {
      header: t('warranty.colAmount'),
      align: 'right',
      renderCell: (c) => (
        <Typography variant="body2" fontWeight={600}>
          {formatPrice(c.claimAmountCents)}
        </Typography>
      ),
    },
    {
      header: t('warranty.colCreated'),
      renderCell: (c) => (
        <Typography variant="body2">{format(new Date(c.createdAt), 'd.M.yyyy', { locale: dateFnsLocale() })}</Typography>
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (c) =>
        c.status === 'rejected' || c.status === 'refunded' ? null : (
          <Tooltip title={tc('moreOptions')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, c);
              }}
              aria-label={tc('moreOptions')}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('warranty.title')}
          </Typography>
        </Box>
        {!isMobile && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormOpen(true); }}>
            {t('warranty.createClaim')}
          </Button>
        )}
      </Box>

      <Card>
        <Box sx={{ px: 2, pt: 2 }}>
          <DataTable<WarrantyClaim>
            columns={columns}
            data={filtered}
            getRowKey={(c) => c.id}
            quickFilters={quickFilters}
            activeFilter={tab}
            onFilterChange={(value) => { setTab(value as TabValue); }}
            paperWrapper={false}
            loading={claimsQuery.isLoading}
            emptyMessage={t('warranty.noClaims')}
            emptyDescription={t('warranty.noClaimsDesc')}
            renderMobileCard={(c) => (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {c.partName}
                  </Typography>
                  <Chip label={statusLabels[c.status]} size="small" color={statusColors[c.status]} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {c.supplierName}
                  </Typography>
                  <Typography variant="body2">{formatPrice(c.claimAmountCents)}</Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {menuClaim?.status === 'open' && (
          <MenuItem onClick={() => { handleTransition(menuClaim, 'submitted'); }}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} />
            {t('warranty.markSubmitted')}
          </MenuItem>
        )}
        {menuClaim?.status === 'submitted' && (
          <MenuItem onClick={() => { handleTransition(menuClaim, 'approved'); }}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
            {t('warranty.markApproved')}
          </MenuItem>
        )}
        {menuClaim?.status === 'submitted' && (
          <MenuItem onClick={() => { handleTransition(menuClaim, 'rejected'); }}>
            <CancelIcon fontSize="small" sx={{ mr: 1 }} />
            {t('warranty.markRejected')}
          </MenuItem>
        )}
        {menuClaim?.status === 'approved' && (
          <MenuItem
            onClick={() => {
              handleTransition(menuClaim, 'refunded', menuClaim.claimAmountCents ?? 0);
            }}
          >
            <PaymentsIcon fontSize="small" sx={{ mr: 1 }} />
            {t('warranty.markRefunded')}
          </MenuItem>
        )}
      </Menu>

      {isMobile && (
        <Fab
          color="primary"
          size="medium"
          onClick={() => { setFormOpen(true); }}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1050 }}
          aria-label={t('warranty.createClaim')}
        >
          <AddIcon />
        </Fab>
      )}

      <WarrantyClaimDialog open={formOpen} onClose={() => { setFormOpen(false); }} />
    </Box>
  );
}
